import React, { useState, useEffect } from 'react';
import Sidebar from '../common/Sidebar';
import { GetProductByIdAdmin, UpdateProductAdmin } from "../../Api/Admin/productServices"
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../component/common/Loader';

const ProductDetails = () => {
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editedProduct, setEditedProduct] = useState(null);

    useEffect(() => {
        const fetchProduct = async () => {
            const token = localStorage.getItem("token");
            try {
                const response = await GetProductByIdAdmin(token, id);
                console.log(response.data.data)
                setProduct(response.data.data);
            } catch (error) {
                console.error('Error fetching product:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id]);

    useEffect(() => {
        if (product) {
            setEditedProduct({
                ...product,
                size: product.size
            });
        }
    }, [product]);

    const handleUpdate = async () => {
        const token = localStorage.getItem("token");
        try {
            const updatedProduct = {
                ...editedProduct,
                size: editedProduct.size,
                inStock: editedProduct.inStock
            };
            await UpdateProductAdmin(token, id, updatedProduct);
            const response = await GetProductByIdAdmin(token, id);
            setProduct(response.data.data);
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating product:', error);
        }
    };

    const handleSizeStockToggle = (sizeIndex) => {
        setEditedProduct(prev => ({
            ...prev,
            size: prev.size.map((sizeItem, idx) => 
                idx === sizeIndex 
                    ? { ...sizeItem, inStock: !sizeItem.inStock }
                    : sizeItem
            )
        }));
    };

    const handleMainStockToggle = () => {
        setEditedProduct(prev => ({
            ...prev,
            inStock: !prev.inStock
        }));
    };

    return (
        <div className="flex">
            <Sidebar />
            {loading ? (
                <div className="w-full ml-[250px] flex items-center justify-center min-h-screen">
                    <Loader />
                </div>
            ) : !product ? (
                <div className="w-full ml-[250px] flex items-center justify-center min-h-screen">
                    Product not found
                </div>
            ) : (
                <div className="p-6 w-full ml-[250px] bg-gray-50 min-h-screen">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold">Product Details</h1>
                        <button
                            onClick={() => isEditing ? handleUpdate() : setIsEditing(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            {isEditing ? 'Save Changes' : 'Edit Product'}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Images Section */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h2 className="text-xl font-semibold mb-4">Product Images</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {product.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={image}
                                        alt={`Product ${index + 1}`}
                                        className="w-full h-48 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Product Information */}
                        <div className="space-y-4">
                            <div className="bg-white p-6 rounded-lg shadow">
                                <h2 className="text-xl font-semibold mb-4">{product.title}</h2>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-gray-600">Original Price</p>
                                        <p className="font-semibold">₹{product.originalPrice}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Discounted Price</p>
                                        <p className="font-semibold">₹{product.discountedPrice}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Discount</p>
                                        <p className="font-semibold">{product.discount}%</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Stock</p>
                                        <p className="font-semibold">{product.stock} units</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Stock Status</p>
                                        {isEditing ? (
                                            <button
                                                onClick={handleMainStockToggle}
                                                className={`px-4 py-2 rounded-lg ${
                                                    editedProduct.inStock
                                                        ? 'bg-green-100 hover:bg-green-200'
                                                        : 'bg-red-100 hover:bg-red-200'
                                                }`}
                                            >
                                                {editedProduct.inStock ? 'In Stock' : 'Out of Stock'}
                                            </button>
                                        ) : (
                                            <p className={`font-semibold ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
                                                {product.inStock ? 'In Stock' : 'Out of Stock'}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 space-y-2">
                                    <div>
                                        <p className="text-gray-600 mb-2">Sizes Available</p>
                                        <div className="flex flex-wrap gap-2">
                                            {editedProduct?.size.map((sizeItem, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => isEditing && handleSizeStockToggle(index)}
                                                    className={`px-4 py-2 border rounded-lg ${
                                                        isEditing
                                                            ? sizeItem.inStock
                                                                ? 'bg-green-100 hover:bg-green-200'
                                                                : 'bg-red-100 hover:bg-red-200'
                                                            : 'hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {sizeItem.size}
                                                    {isEditing && (
                                                        <span className="ml-2">
                                                            ({sizeItem.inStock ? 'In Stock' : 'Out of Stock'})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Color</p>
                                        <p className="font-semibold">{product.color}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Material</p>
                                        <p className="font-semibold">{product.material}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Brand</p>
                                        <p className="font-semibold">{product.brand}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Category</p>
                                        <p className="font-semibold capitalize">{product.category}</p>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="text-gray-600">Description</p>
                                    <p className="mt-1">{product.description}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetails;